import React from 'react';
import styles from './CarouselTable.module.scss';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import table_sorting_icon from '../../../styles/images/table_sorting_icon.svg';

const CarouselTable = ({ carouselList = [], onDelete, onReorder, onBannerClick, isReorderLoading = false }) => {
  const [isPositionDescending, setIsPositionDescending] = React.useState(false);

  // Reverses only the relative order of Live banners, leaving every non-Live
  // banner in its original slot untouched. Self-inverse: applying it twice
  // (sort, then un-sort after a drag) restores the ascending order.
  const reverseLiveOnly = (list) => {
    const liveIndices = [];
    list.forEach((item, idx) => {
      if (item.publishStatus === 'Live') {
        liveIndices.push(idx);
      }
    });
    const reversedLiveItems = liveIndices.map((idx) => list[idx]).reverse();
    const result = list.slice();
    liveIndices.forEach((idx, i) => {
      result[idx] = reversedLiveItems[i];
    });
    return result;
  };

  const displayList = isPositionDescending ? reverseLiveOnly(carouselList) : carouselList;

  const liveRankMap = React.useMemo(() => {
    const map = new Map();
    let rank = 0;
    carouselList.forEach((item) => {
      if (item.publishStatus === 'Live') {
        rank += 1;
        map.set(item.id, rank);
      }
    });
    return map;
  }, [carouselList]);

  const togglePositionSort = () => {
    setIsPositionDescending((prev) => !prev);
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const items = reorder(displayList, result.source.index, result.destination.index);
    const finalList = isPositionDescending ? reverseLiveOnly(items) : items;
    onReorder(finalList);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const getItemStyle = (isDragging, draggableStyle, isDragDisabled) => ({
    userSelect: 'none',
    left: '0px',
    top: '72px',
    pointerEvents: 'auto',
    cursor: isDragDisabled ? 'default' : (isDragging ? 'all-scroll' : 'pointer'),
    ...draggableStyle,
  });

  const getListStyle = (isDraggingOver) => ({
    cursor: 'all-scroll',
  });

  const handleDelete = (event, id) => {
    event.stopPropagation(); // Prevent row click from firing
    onDelete(id);
  };

  const handleRowClick = (bannerId) => {
    if (onBannerClick) {
      onBannerClick(bannerId);
    }
  };

  const SkeletonRow = () => (
    <div className={styles.row_outer_wrapper}>
      <div className={[styles.tbl_row, styles.skeleton_row].join(' ')}>
        {/* Drag Handle */}
        <div className={styles.checkbox_wrapper}>
          <div className={styles.skeleton_box}></div>
        </div>

        {/* Thumbnail */}
        <div className={styles.tbl_data}>
          <div className={styles.inner_data}>
            <div className={[styles.skeleton_thumbnail, styles.skeleton_animate].join(' ')}></div>
          </div>
        </div>

        <div className={styles.tbl_data_wrapper}>
          {/* Header */}
          <div className={styles.tbl_data}>
            <div className={styles.inner_data}>
              <div className={[styles.skeleton_text, styles.skeleton_animate].join(' ')}></div>
              <div className={[styles.skeleton_text, styles.skeleton_text_short, styles.skeleton_animate].join(' ')}></div>
            </div>
          </div>

          {/* Position */}
          <div className={styles.tbl_data}>
            <div className={styles.inner_data}>
              <div className={[styles.skeleton_badge, styles.skeleton_animate].join(' ')}></div>
            </div>
          </div>

          {/* Status */}
          <div className={styles.tbl_data}>
            <div className={styles.inner_data}>
              <div className={[styles.skeleton_status, styles.skeleton_animate].join(' ')}></div>
            </div>
          </div>

          {/* Actions */}
          <div className={styles.tbl_data}>
            <div className={styles.inner_data}>
              <div className={styles.icon_wrapper}>
                <div className={[styles.skeleton_icon, styles.skeleton_animate].join(' ')}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const SortIcon = (
    <img
      src={table_sorting_icon}
      alt="sort-icon"
      style={{ cursor: 'pointer', marginLeft: '8px' }}
    />
  );

  return (
    <div className={[styles.carousel_table_wrapper].join(' ')}>
      <div className={styles.main_wrapper}>
        <div className={styles.inner_wrapper}>
          <div className={[styles.table_main_wrapper, styles.table_reduced_width].join(' ')}>
            <div className={styles.table_container}>
              {/* Table Header */}
              <div className={styles.tbl_header}>
                <div className={styles.tbl_head}>Thumbnail</div>
                <div className={styles.tbl_head_wrapper}>
                  <div className={styles.tbl_head}>Header</div>
                  <div
                    className={styles.tbl_head}
                    onClick={togglePositionSort}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    <span>Position</span>
                    {SortIcon}
                  </div>
                  <div className={styles.tbl_head}>Publish Status</div>
                  <div className={styles.tbl_head}></div>
                </div>
              </div>

              {/* Table Body */}
              <div className={styles.tbl_body}>
                <div className={[styles.tbl_row_wrapper].join(' ')}>
                  {isReorderLoading ? (
                    <>
                      {displayList.map((_, index) => (
                        <SkeletonRow key={`skeleton-${index}`} />
                      ))}
                    </>
                  ) : displayList.length === 0 ? (
                    <div className={styles.empty_state}>
                      <div className={styles.empty_text}>No banners added yet</div>
                      <div className={styles.empty_subtext}>
                        Click &quot;Add Banner&quot; to create your first carousel banner
                      </div>
                    </div>
                  ) : (
                    <DragDropContext
                      onDragEnd={onDragEnd}
                    >
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="droppable"
                            style={getListStyle(snapshot.isDraggingOver)}
                          >
                            {displayList.map((item, index) => (
                              <Draggable
                                key={item.id}
                                draggableId={`${item.id}`}
                                index={index}
                                isDragDisabled={item.publishStatus !== 'Live'}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={getItemStyle(
                                      snapshot.isDragging,
                                      provided.draggableProps.style,
                                      item.publishStatus !== 'Live',
                                    )}
                                    className={`item ${
                                      snapshot.isDragging ? 'dragging' : ''
                                    }`}
                                  >
                                    <div className={[styles.row_outer_wrapper].join(' ')}>
                                      <div
                                        id={item.id}
                                        className={[styles.tbl_row, styles.selected].join(' ')}
                                        onClick={() => handleRowClick(item.id)}
                                      >
                                        {/* Drag Handle */}
                                        <div
                                          className={[
                                            styles.checkbox_wrapper,
                                            snapshot.isDragging ? styles.active : '',
                                            item.publishStatus !== 'Live' ? styles.disabled : '',
                                          ].join(' ')}
                                        >
                                          <div className={`${styles.check_box}`}></div>
                                        </div>

                                        {/* Thumbnail */}
                                        <div className={styles.tbl_data}>
                                          <div className={styles.inner_data}>
                                            {item.thumbnail ? (
                                              <img
                                                className={styles.img}
                                                src={item.thumbnail}
                                                alt={item.header}
                                              />
                                            ) : (
                                              <div className={styles.empty_state_img}></div>
                                            )}
                                          </div>
                                        </div>

                                        <div className={styles.tbl_data_wrapper}>
                                          {/* Header */}
                                          <div className={styles.tbl_data}>
                                            <div className={styles.inner_data}>
                                              <div className={[styles.txt].join(' ')}>
                                                {item.header || 'NA'}
                                              </div>
                                            </div>
                                          </div>

                                          {/* Position */}
                                          <div className={styles.tbl_data}>
                                            <div className={styles.inner_data}>
                                              {item.publishStatus === 'Live' && (
                                                <div className={[styles.txt].join(' ')}>
                                                  {liveRankMap.get(item.id)}
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          {/* Publish Status */}
                                          <div className={styles.tbl_data}>
                                            <div className={styles.inner_data}>
                                              <div
                                                className={`${styles.status} ${
                                                  styles[item?.publishStatus?.toLowerCase()]
                                                }`}
                                              >
                                                <div className={styles.status_txt}>
                                                  {item.publishStatus || 'Draft'}
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Actions */}
                                          <div className={styles.tbl_data}>
                                            <div className={styles.inner_data}>
                                              <div className={styles.icon_wrapper}>
                                                <div
                                                  className={styles.delete_icon}
                                                  onClick={(e) => handleDelete(e, item.id)}
                                                  title="Delete banner"
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarouselTable;
